import { create } from 'zustand';
import { INITIAL_PUBLISHER_CREDIT_BALANCE, PUBLISHER_CREDIT_PACKS } from '../data/publisherCredits';
import { availableCredits } from '../lib/credits';
import type { CreditBalance, CreditPack } from '../types/domain';

interface PublisherCreditsState extends CreditBalance {
  selectedPack: string;
  setSelectedPack: (packId: string) => void;
  grant: (amount: number) => void;
  spend: (amount: number) => boolean;
  hydrate: (balance: CreditBalance) => void;
  reset: () => void;
}

export const usePublisherCreditsStore = create<PublisherCreditsState>((set) => ({
  ...INITIAL_PUBLISHER_CREDIT_BALANCE,
  reserved: INITIAL_PUBLISHER_CREDIT_BALANCE.reserved ?? 0,
  selectedPack: PUBLISHER_CREDIT_PACKS[1]?.id ?? PUBLISHER_CREDIT_PACKS[0]?.id ?? '',

  setSelectedPack(selectedPack) {
    set({ selectedPack });
  },

  grant(amount) {
    if (amount <= 0) return;
    set((current) => ({ total: current.total + amount }));
  },

  spend(amount) {
    if (!Number.isFinite(amount) || amount <= 0) return true;
    let accepted = false;
    set((current) => {
      const available = availableCredits(current);
      if (available < amount) return current;
      accepted = true;
      return { used: current.used + amount };
    });
    return accepted;
  },

  hydrate(balance) {
    set({ ...balance, reserved: balance.reserved ?? 0 });
  },

  reset() {
    set({
      ...INITIAL_PUBLISHER_CREDIT_BALANCE,
      reserved: INITIAL_PUBLISHER_CREDIT_BALANCE.reserved ?? 0,
      selectedPack: PUBLISHER_CREDIT_PACKS[1]?.id ?? PUBLISHER_CREDIT_PACKS[0]?.id ?? '',
    });
  },
}));

export function publisherCreditsLeft(balance: CreditBalance): number {
  return availableCredits(balance);
}

export function publisherCreditPack(packId: string): CreditPack {
  return PUBLISHER_CREDIT_PACKS.find((pack) => pack.id === packId) ?? PUBLISHER_CREDIT_PACKS[0];
}
